import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import { useToast } from "../../contexts/ToastContext.tsx";
import Icon from "../../core/components/Icon.tsx";
import Separator from "../../core/components/Separator.tsx";
import { formatMarkdown } from "../../core/utils/markdownhelper.ts";
import useService from "../../hooks/useService.ts";
import {
	getIaLocalizacaoConjunto,
	getIaResumoViagem,
	postIaLegislacao,
} from "../../services/InteligenciaArtificial/InteligenciaArtificial.ts";
import IAHelpButton from "../IaHelpButton/IaHelpButton.tsx";
import LoadingIa from "../LoadingIa/LoadingIa.tsx";
import "./IaContainer.css";

enum IaMode {
	LOCALIZACAO = "localizacao",
	RESUMO = "resumo",
	LEGISLACAO = "legislacao",
}

enum StateKey {
	INITIAL = "initial",
	LOADING = "loading",
	EMPTY = "empty",
	ERROR = "error",
}

const title: Record<IaMode, string> = {
	[IaMode.LOCALIZACAO]: "Localizar veículo",
	[IaMode.RESUMO]: "Gerar resumo da viagem",
	[IaMode.LEGISLACAO]: "Tirar dúvida sobre legislação",
};

const IaDelay = 5000;

function IaContainer() {
	const { profile } = useProfile();
	const { showToast } = useToast();

	const timeoutIdRef = useRef<number | null>(null);
	const lastRequestedModeRef = useRef<IaMode | null>(null);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	const clearPendingTimeout = useCallback(() => {
		if (timeoutIdRef.current) {
			clearTimeout(timeoutIdRef.current);
			timeoutIdRef.current = null;
		}
	}, []);

	const scheduleTimeout = useCallback((cb: () => void) => {
		clearPendingTimeout();
		timeoutIdRef.current = window.setTimeout(() => {
			cb();
			timeoutIdRef.current = null;
		}, IaDelay);
	}, [clearPendingTimeout]);

	const [modoIa, setModoIa] = useState<IaMode>(IaMode.LOCALIZACAO);
	const [textoEntrada, setTextoEntrada] = useState<string>("");
	const [respostasIa, setRespostasIa] = useState<Record<IaMode, string | null>>({
		[IaMode.LOCALIZACAO]: null,
		[IaMode.RESUMO]: null,
		[IaMode.LEGISLACAO]: null,
	});
	const [errosIa, setErrosIa] = useState<Record<IaMode, string | null>>({
		[IaMode.LOCALIZACAO]: null,
		[IaMode.RESUMO]: null,
		[IaMode.LEGISLACAO]: null,
	}); const [showTourTabs, setShowTourTabs] = useState<boolean>(() => {
		if (typeof window === "undefined") return false;
		return !localStorage.getItem("iaTourTabsV1");
	});
	const [viewState, setViewState] = useState<StateKey>(StateKey.INITIAL);
	const [textareaHeight, setTextareaHeight] = useState(40);

	const handleApiError = useCallback((err: unknown) => {
		const status = axios.isAxiosError(err) ? err.response?.status : undefined;
		const modo = lastRequestedModeRef.current;
		if (!modo) return;

		scheduleTimeout(() => {
			const msg =
				status === 503
					? "Serviço de IA temporariamente indisponível. Tente novamente em alguns minutos."
					: status === 401
						? "Sessão expirada. Faça login novamente e tente outra vez."
						: "Ocorreu um erro ao consultar a IA. Tente novamente em alguns instantes.";
			setErrosIa((prev) => ({ ...prev, [modo]: msg }));
			setViewState(StateKey.ERROR);
		});
	}, [scheduleTimeout]);

	const [isLoadingResumo, fetchResumo] = useService(
		getIaResumoViagem,
		{
			autoStart: false,
			onData: ({ data }) => {
				if (lastRequestedModeRef.current !== IaMode.RESUMO || modoIa !== IaMode.RESUMO) return;
				const resposta =
					data?.resposta ??
					data?.v ??
					"Nenhuma resposta foi retornada pela IA para essa solicitação.";
				scheduleTimeout(() => {
					setRespostasIa((prev) => ({ ...prev, [IaMode.RESUMO]: resposta }));
					setViewState(StateKey.INITIAL);
				});
			},
			onError: handleApiError,
		}
	)
	const [isLoadingLocalizacao, fetchLocalizacao] = useService(
		getIaLocalizacaoConjunto,
		{
			autoStart: false,
			onData: ({ data }) => {
				if (lastRequestedModeRef.current !== IaMode.LOCALIZACAO || modoIa !== IaMode.LOCALIZACAO) return;
				const resposta =
					data?.resposta ??
					data?.v ??
					"Nenhuma resposta foi retornada pela IA para essa solicitação.";
				scheduleTimeout(() => {
					setRespostasIa((prev) => ({ ...prev, [IaMode.LOCALIZACAO]: resposta }));
					setViewState(StateKey.INITIAL);
				});
			},
			onError: handleApiError,
		}
	)
	const [isLoadingLegislacao, fetchLegislacao] = useService(
		postIaLegislacao,
		{
			autoStart: false,
			onData: ({ data }) => {
				if (lastRequestedModeRef.current !== IaMode.LEGISLACAO || modoIa !== IaMode.LEGISLACAO) return;
				const resposta =
					data?.resposta ??
					data?.v ??
					"Nenhuma resposta foi retornada pela IA para essa solicitação.";
				scheduleTimeout(() => {
					setRespostasIa((prev) => ({ ...prev, [IaMode.LEGISLACAO]: resposta }));
					setViewState(StateKey.INITIAL);
				});
			},
			onError: handleApiError,
		}
	)

	const fecharTourTabs = () => {
		if (typeof window !== "undefined") {
			localStorage.setItem("iaTourTabsV1", "1");
		}
		setShowTourTabs(false);
	};

	const getLabelTexto = useCallback(() => {
		switch (modoIa) {
			case IaMode.RESUMO:
				return "Informe o CT-e ou Load e gere o resumo da viagem utilizando inteligência artificial:";
			case IaMode.LOCALIZACAO:
				return "Quero saber a última localização estimada do veículo com placa:";
			case IaMode.LEGISLACAO:
				return "Quero tirar uma dúvida sobre legislação de transporte:";
			default:
				return "";
		}
	}, [modoIa]);

	const getPlaceholder = useCallback(() => {
		switch (modoIa) {
			case "resumo":
				return "Ex.: 123456 ou TS-000123";
			case "localizacao":
				return "Ex.: ABC1D23";
			case "legislacao":
				return "Descreva sua dúvida sobre legislação de transporte...";
			default:
				return "";
		}
	}, [modoIa]);

	const handleChangeModo = useCallback((novoModo: IaMode) => {
		setModoIa(novoModo);
		setViewState(StateKey.INITIAL);
		setErrosIa((prev) => ({ ...prev, [novoModo]: null }));
		setTextoEntrada("");
	}, []);

	const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
		if (!profile?.token) {
			showToast("Token de usuário não encontrado.", "error");
			return;
		}
		event.preventDefault();

		const valor = textoEntrada.trim();
		if (!valor) return;;
		setErrosIa((prev) => ({ ...prev, [modoIa]: null }));
		setViewState(StateKey.INITIAL);
		lastRequestedModeRef.current = modoIa;

		if (modoIa === IaMode.RESUMO) {
			fetchResumo({ token: profile.token, identificadorViagem: valor })
		} else if (modoIa === IaMode.LOCALIZACAO) {
			fetchLocalizacao({ token: profile.token, placa: valor });
		} else {
			fetchLegislacao({ token: profile.token, pergunta: valor });
		}
	}, [modoIa, textoEntrada, profile, fetchResumo, fetchLocalizacao, fetchLegislacao, showToast]);


	const handleTextareaChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const input = e.target;
		setTextoEntrada(input.value);
		input.style.height = "40px";
		const newHeight = Math.max(40, input.scrollHeight);
		setTextareaHeight((prevHeight) => (newHeight !== prevHeight ? newHeight : prevHeight));
	}, []);

	const isLoading = useMemo(() => {
		return isLoadingResumo || isLoadingLocalizacao || isLoadingLegislacao || !profile?.token;
	}, [isLoadingResumo, isLoadingLocalizacao, isLoadingLegislacao, profile]);

	const respostaIaAtual = useMemo(() => {
		return respostasIa[modoIa];
	}, [respostasIa, modoIa]);

	const IAHelpButtonLabel = useMemo(() => {
		if (modoIa === IaMode.RESUMO) {
			return "Gerar resumo";
		}
		if (modoIa === IaMode.LOCALIZACAO) {
			return "Localizar veículo";
		}
		return "Tirar dúvida";
	}, [modoIa]);

	useEffect(() => {
		if (isLoading) {
			setViewState(StateKey.LOADING);
		}
	}, [isLoading]);

	useEffect(() => {
		return () => {
			clearPendingTimeout();
		};
	}, [clearPendingTimeout]);

	useLayoutEffect(() => {
		if (textareaRef.current) {
			const textarea = textareaRef.current;
			textarea.style.height = "40px";
			const newHeight = Math.max(40, textarea.scrollHeight);
			setTextareaHeight(newHeight);
		}
	}, [modoIa]);

	return (
		<motion.div
			animate={{ opacity: 1 }}
			initial={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
			className="ia-container"
		>
			<div className="ia-header">
				<div className="ia-header-row">
					<Icon name="Sparkles" size="2xl" theme="darkGrey" fill="#444444" strokeWidth={1} />
					<h2>{title[modoIa]}</h2>
					<motion.div layout className="ia-info-tooltip">
						<div className="ia-info-icon">i</div>
						<div className="ia-info-tooltip-content">
							Aqui você pode:
							<br />
							• Gerar resumos de viagens a partir do CT-e ou Load;
							<br />
							• Consultar a última localização estimada de um veículo pela
							placa;
							<br />• Tirar dúvidas sobre legislação e operações de transporte
							rodoviário de cargas.
						</div>
					</motion.div>
				</div>
				<Separator size="xs" />
				<form className="ia-form" onSubmit={handleSubmit}>
					<div className="ia-tabs-row">
						<div className="ia-tabs">
							<button
								type="button"
								className={
									"ia-tab" + (modoIa === IaMode.LOCALIZACAO ? " ia-tab-active" : "")
								}
								disabled={StateKey.LOADING === viewState}
								onClick={() => handleChangeModo(IaMode.LOCALIZACAO)}
							>
								Localizar veículo
							</button>
							<button
								type="button"
								className={
									"ia-tab" + (modoIa === IaMode.RESUMO ? " ia-tab-active" : "")
								}
								disabled={StateKey.LOADING === viewState}
								onClick={() => handleChangeModo(IaMode.RESUMO)}
							>
								Resumo da viagem
							</button>
							<button
								type="button"
								className={
									"ia-tab" + (modoIa === IaMode.LEGISLACAO ? " ia-tab-active" : "")
								}
								disabled={StateKey.LOADING === viewState}
								onClick={() => handleChangeModo(IaMode.LEGISLACAO)}
							>
								Legislação de transporte
							</button>
						</div>

						{showTourTabs && (
							<div className="ia-tour-tooltip ia-tour-tooltip-tabs">
								<div className="ia-tour-tooltip-body">
									<strong>Novo recurso</strong>
									<p>
										Use estas abas para escolher o tipo de ajuda da IA:
										<br />
										<span>• Localizar veículo pela placa</span>
										<br />
										<span>• Gerar resumo da viagem por CT-e / Load</span>
										<br />
										<span>• Tirar dúvidas sobre legislação de transporte</span>
									</p>
									<button
										type="button"
										className="ia-tour-close"
										onClick={fecharTourTabs}
									>
										Entendi
									</button>
								</div>
								<div className="ia-tour-tooltip-arrow" />
							</div>
						)}
					</div>

					<label className="ia-label">{getLabelTexto()}</label>

					<motion.div layout="size" layoutId="ia-input-wrapper"
						className="ia-input-row">
						<motion.textarea
							ref={textareaRef}
							animate={{ height: textareaHeight }}
							transition={{ duration: 0.2, ease: "easeOut" }}
							className="ia-input ia-input-multiline"
							placeholder={getPlaceholder()}
							value={textoEntrada}
							onChange={handleTextareaChange}
							style={{ overflow: "hidden", resize: "none", minWidth: "225px" }}
							layout="size"
						/>
						<motion.div layout className="ia-input-button-wrapper"
							style={{
								width: window.innerWidth > 600 ? "fit-content" : "100%",
							}}
						>
							<IAHelpButton
								type="submit"
								disabled={!textoEntrada.trim()}
								loading={viewState === StateKey.LOADING || isLoading}
								fullWidth
							>
								{IAHelpButtonLabel}
							</IAHelpButton>
						</motion.div>
					</motion.div>
				</form>
			</div >

			<motion.div layout="size" className="ia-response-container" transition={{ layout: { duration: 0.24, ease: "easeOut" } }}>
				<AnimatePresence mode="popLayout">
					{viewState === StateKey.ERROR && (
						<motion.div
							key={StateKey.ERROR}
							className="ia-error"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							{errosIa[modoIa]}
						</motion.div>
					)}
					{viewState === StateKey.LOADING && (
						<motion.div
							key={StateKey.LOADING}
							className="ia-loading"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						>
							<div style={{
								display: "flex",
								alignItems: "center",
								gap: "8px"
							}}>
								<div className="ia-spinner" />
								<span>Consultando a IA...</span>
							</div>
							<LoadingIa height="150px" />
						</motion.div>
					)}
					{respostaIaAtual && viewState === StateKey.INITIAL && (
						<motion.div
							key={`${modoIa}-${StateKey.INITIAL}`}
							className="ia-result-card"
							layout="size"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{
								layout: { duration: 0.24, ease: "easeOut" },
								opacity: { duration: 0.16 }
							}}
						>
							<div className="ia-result-header">
								<span className="ia-pill">
									{modoIa === IaMode.RESUMO
										? "Resumo da IA"
										: modoIa === IaMode.LOCALIZACAO
											? "Localização estimada"
											: "Resposta sobre legislação"}
								</span>
							</div>
							<p
								className="ia-result-text"
								dangerouslySetInnerHTML={{ __html: formatMarkdown(respostaIaAtual || "") }}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</motion.div>
		</motion.div >
	);
}

export default IaContainer;
